/**
 * App Configuration System
 *
 * Centralized configuration for app branding and SEO.
 * Customize these values to brand the template for any SaaS application.
 */

/**
 * Single navigation/footer link
 */
export interface AppLink {
  label: string
  href: string
}

/**
 * SEO configuration
 */
export interface SeoConfig {
  titleTemplate: string
  defaultDescription: string
  keywords: string[]
}

/**
 * Theme customization (color overrides)
 */
export interface ThemeConfig {
  primaryColor?: string
  secondaryColor?: string
}

/**
 * Complete app configuration interface
 */
export interface AppConfig {
  /** Application name displayed in header, footer, and meta tags */
  name: string
  /** Short tagline displayed below the name */
  tagline: string
  /** URL to the app logo (optional, defaults to icon) */
  logo?: string
  /** Icon name for the app (Iconify Solar format, e.g., 'solar:box-bold') */
  icon: string
  /** Copyright text for footer (plain text) */
  copyright: string
  /** Copyright owner name */
  copyrightOwner: string
  /** Copyright owner URL */
  copyrightOwnerUrl: string
  /**
   * URL of the admin panel. It lives on its own host (EasyAdmin, served by
   * the API), so it is an external link rather than a Vue route.
   */
  adminUrl: string
  /**
   * Where the Google sign-in button navigates the whole page to.
   *
   * Its own value rather than something built from the API URL: that one is a
   * relative "/api" in development, proxied by the Vite dev server, and an
   * absolute origin in production. A full-page navigation to a relative path
   * would land on the frontend host, which serves no API - a config that only
   * breaks once deployed, with every test still green.
   *
   * **Empty when unconfigured, and empty means the feature is off.** There is
   * deliberately no localhost fallback here: an image built without the build
   * arg would otherwise render a button that navigates a production visitor to
   * `http://api.localhost/...`, which resolves to nothing on their machine.
   * GoogleSignInButton renders nothing at all on an empty value, so an
   * unconfigured deployment hides the door instead of offering a broken one.
   */
  googleOAuthUrl: string

  /** SEO configuration */
  seo: SeoConfig

  /** Theme customization */
  theme: ThemeConfig
}

/**
 * Default app configuration
 *
 * Customize these values for your SaaS application.
 * Environment variables can be used to override sensitive or deployment-specific values.
 */
export const appConfig: AppConfig = {
  name: import.meta.env.VITE_APP_NAME ?? 'My SaaS App',
  tagline: import.meta.env.VITE_APP_TAGLINE ?? 'The best solution for your needs',
  icon: 'solar:box-bold',
  copyright: `\u00A9 ${new Date().getFullYear()} AppsByLuke. All rights reserved.`,
  copyrightOwner: 'AppsByLuke',
  copyrightOwnerUrl: 'https://appsbyluke.com',
  // `||` not `??`: the Docker build arg defaults to an empty string, which
  // would otherwise render the admin link with a blank href.
  adminUrl: import.meta.env.VITE_ADMIN_URL || 'http://admin.localhost/admin',
  // No fallback, unlike adminUrl. A dev-host default baked into a production
  // image is worse than an absent button: the person clicks, the browser goes
  // to a host that does not exist for them, and nothing says why. Empty here
  // means GoogleSignInButton renders nothing. Development gets the value from
  // docker-compose.yml, production from the VITE_GOOGLE_OAUTH_URL build arg.
  googleOAuthUrl: import.meta.env.VITE_GOOGLE_OAUTH_URL ?? '',

  seo: {
    titleTemplate: `%s | ${import.meta.env.VITE_APP_NAME ?? 'My SaaS App'}`,
    defaultDescription:
      'A powerful SaaS platform that helps you achieve your goals faster and more efficiently.',
    keywords: ['saas', 'platform', 'productivity', 'business', 'software'],
  },

  theme: {
    primaryColor: undefined, // Uses default theme primary
    secondaryColor: undefined, // Uses default theme secondary
  },
}
